import React, { useCallback, useEffect, useState } from 'react';
import { notification } from 'antd';
import { OpFormDrawer } from 'components/customAntd/DLS/OpFormDrawer/OpFormDrawer';
import { IOnSubmitArgs, OpForm } from 'components/customAntd/DLS/OpForm/OpForm';
import { EmailTemplate } from 'types/emailTemplateTypes';
import { useTranslation } from 'react-i18next';
import { OpTabs } from 'components/customAntd/DLS/OpTabs/OpTabs';
import TemplateForm from './tabs/TemplateForm';
import FormatForm, { getRawTemplateHTML } from './tabs/FormatForm';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/store';
import { patchRequest, postRequest } from 'api/apiClient';
import { fetchEmailTemplates } from 'store/slices/emailTemplatesSlice';
import { hasPermission } from 'utils/utils';

interface Props {
  open: boolean;
  onClose: () => void;
  selectedTemplate: EmailTemplate | null;
}

const EmailTemplateDrawer: React.FC<Props> = ({ open, onClose, selectedTemplate }) => {
  const dispatch: AppDispatch = useDispatch();
  const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
  const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);
  const hasEmailTemplatesWrite = hasPermission(tokenScopeList, orgId, 'o', 'emailtemplates:w');

  const [form] = OpForm.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeKey, setActiveKey] = useState('template');

  const { t } = useTranslation();

  useEffect(() => {
    if (open) {
      setActiveKey('template');
      if (!selectedTemplate) {
        form.resetFields();
        form.setFieldsValue({ content: '' });
      } else {
        form.setFieldsValue(selectedTemplate);
      }
    }

  }, [open, form, selectedTemplate]);

  const tabItems = [
    {
      key: 'template',
      label: t('emailTemplates.tabs.template'),
      children: <TemplateForm />,
      forceRender: true
    },
    {
      key: 'format',
      label: t('emailTemplates.tabs.format'),
      children: <FormatForm form={form} open={open} hasEmailTemplatesWrite={hasEmailTemplatesWrite} />,
      forceRender: true
    },
  ];

  const handleSubmit = useCallback(async ({ values }: IOnSubmitArgs) => {
    setIsSubmitting(true);
    try {
      const finalValues = {
        ...values,
        content: getRawTemplateHTML(form),
      }

      if (selectedTemplate?.id) {
        await patchRequest(`/orgs/${orgId}/emailTemplates/${selectedTemplate.id}`, finalValues);
        notification.success({
          message: t('notification.success'),
          description: t('emailTemplates.notification.updateSuccess'),
          placement: 'bottomRight',
        });
      } else {
        await postRequest(`/orgs/${orgId}/emailTemplates`, finalValues);
        notification.success({
          message: t('notification.success'),
          description: t('emailTemplates.notification.createSuccess'),
          placement: 'bottomRight',
        });
      }
      await dispatch(fetchEmailTemplates({ orgId }));
      form.resetFields();
      form.setFieldsValue({ content: '' });
      onClose();
    } catch (error) {
      notification.error({
        message: t('notification.error'),
        description: t('emailTemplates.notification.saveError'),
        placement: 'bottomRight',
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [form, orgId, onClose, selectedTemplate, dispatch, t])

  return (
    <OpFormDrawer
      form={form}
      open={open}
      width={850}
      onClose={onClose}
      title={selectedTemplate ? selectedTemplate.name : t('emailTemplates.newTemplate')}
      isFormLoading={isSubmitting}
      isFormReadOnly={!hasEmailTemplatesWrite}
      formComponent={
        <OpForm
          form={form}
          initialValues={{
            name: '',
            content: '',
            styleFrameColor: '#EEEEEE',
            styleButtonColor: '#035EB8',
            styleButtonTextColor: '#FFFFFF',
            ...(selectedTemplate || {})
          }}
          onSubmit={handleSubmit}
          hasError={false}
          defaultButtons={false}
          isReadOnly={!hasEmailTemplatesWrite}
        >
          <OpTabs activeKey={activeKey} onChange={setActiveKey} items={tabItems} />
        </OpForm>
      }
    />
  );
};

export default EmailTemplateDrawer;
